import React, { ReactElement } from 'react';
import { makeStyles } from '@mui/styles';
import { InputLabel, MenuItem, FormControl, Select, SelectProps } from '@mui/material';
import StyledBaseInput from './StyledBaseInput';

export interface DropDownOptions {
  label: string | ReactElement;
  value: any;
}

type DropdownProps = Omit<SelectProps, 'onChange' | 'error' | 'renderValue'> & {
  options: Array<DropDownOptions>;
  label?: string | ReactElement;
  onChange: (name: string, value: any) => void;
  getRenderValue?: (selected: any[], options: any[], id: string) => any;
  getRenderOptions?: (options: any[], id: string) => any;
  error?: string | undefined;
  id?: string;
  labelProps?: any;
  additionalRenderItems?: any;
};

const makeRenderValue = (selected: any = [], options: any = [], id = 'id') => {
  if (!options.length || !selected.length) return '';

  const result = selected.map(x => options.find(y => y?.value?.[id] === x)?.label ?? '').join(',');

  return result;
};

const defaultRenderOptions = (options: any = [], id = 'id') =>
  options.map(
    (option, idx): ReactElement => (
      <MenuItem key={`${idx}-dropdown-${id}-item`} value={option.value[id]}>
        {option.label}
      </MenuItem>
    )
  );

const MultiSelectDropdown: React.FC<DropdownProps> = ({
  id = 'id',
  getRenderValue = makeRenderValue,
  getRenderOptions = defaultRenderOptions,
  label,
  onChange,
  value,
  options,
  error,
  labelProps,
  additionalRenderItems,
  ...props
}) => {
  const classes = useStyles()();
  const handleChange = e => {
    const {
      target: { name, value }
    } = e;
    return onChange(name, value);
  };

  return (
    <FormControl variant="standard" className={classes.select}>
      {label && (
        <InputLabel {...labelProps} id={`${id}-select-label`} shrink={false}>
          {label}
        </InputLabel>
      )}
      <Select
        multiple
        labelId={`${id}-select-label`}
        value={value}
        renderValue={(selected: any) => getRenderValue(selected, options, id)}
        onChange={handleChange}
        className={classes.select}
        error={Boolean(error)}
        input={<StyledBaseInput />}
        inputProps={{ ...(props.inputProps as any) }}
        sx={{
          borderRadius: '8px',
          fontWeight: 300,
          fontSize: '16px',
          width: '650px'
        }}
        {...props}
      >
        {additionalRenderItems}
        {getRenderOptions(options, id)}
      </Select>
    </FormControl>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    select: {
      height: '70px',
      borderRadius: '8px',
      fontWeight: '300 !important',
      width: '650px',
      '&.MuiSelect-select': {
        backgroundColor: '#FFFFFF',
        '&:focus': {
          backgroundColor: '#FFFFFF'
        }
      }
    }
  }));

export default MultiSelectDropdown;
